import {treasuryIdempotencyKey} from './treasury-write-request.js';
export class TreasuryExpenseRequestContractError extends Error {
  constructor() {super('Choose a valid expense, revision and required fields.'); this.name = 'TreasuryExpenseRequestContractError'; this.code = 'INVALID_API_REQUEST';}
}
const fail = () => {throw new TreasuryExpenseRequestContractError();};
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
export const readExpenseId = value => {if (typeof value !== 'string' || !/^[a-f\d]{24}$/i.test(value)) fail(); return value;};
const text = (value, max, required = true) => {if (value == null && !required) return; if (typeof value !== 'string' || value.trim().length > max || required && !value.trim()) fail();};
const revision = value => {if (!Number.isSafeInteger(value) || value < 0) fail();};
const amount = (value, zero = false) => {
  if (!['string', 'number'].includes(typeof value) || !(zero ? /^\d+(?:\.\d{1,2})?$/ : /^\d{1,9}(?:\.\d{1,2})?$/).test(zero ? String(value).trim() : String(value))) fail();
  const minor = Math.round(Number(value) * 100); if (!Number.isSafeInteger(minor) || minor < (zero ? 0 : 1) || zero && minor > 100000000000) fail();
};
const date = value => {if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) fail();};
export const expenseActions = ['payment_failed', 'amend', 'comment', 'start_review', 'resubmit', 'request_information', 'approve', 'submit_claim', 'start_payment', 'mark_paid', 'withdraw', 'decline', 'submit_settlement', 'request_settlement_information', 'approve_settlement', 'declare_return', 'record_return', 'complete_advance'];
export function readExpenseQuery(input = {}) {
  if (!record(input)) fail();
  if (input.before != null && input.before !== '') readExpenseId(input.before);
  if (input.view !== undefined && input.view !== '' && input.view !== 'mine') fail();
  return input;
}
export function readExpenseRequest(action, input, options = {}) {
  if (!record(input) || !['create', 'receipt', ...expenseActions].includes(action)) fail();
  text(input.note, 4000, false);
  const key = treasuryIdempotencyKey(input, options);
  if (key !== undefined && (typeof key !== 'string' || !/^[a-zA-Z0-9_-]{8,128}$/.test(key))) fail();
  if (action === 'create') {
    if (!['reimbursement', 'preapproval', 'advance'].includes(input.kind)) fail();
    if (input.categoryId !== undefined) {
      readExpenseId(input.categoryId);
      if (input.fundId !== undefined || input.title !== undefined) fail();
    } else {readExpenseId(input.fundId); text(input.title, 160);}
    amount(input.amount); text(input.description, 4000);
    if (input.source != null) {if (!record(input.source) || !['activity', 'session', 'event'].includes(input.source.type)) fail(); readExpenseId(input.source.id);}
  } else {
    revision(input.revision);
    if (['comment', 'request_information', 'withdraw', 'decline'].includes(action)) text(input.note, 4000);
    if (action === 'payment_failed') {text(input.note, 4000); if (input.confirmDecision !== true) fail();}
    if (action === 'approve') {
      for (const field of ['approvalEvidenceId', 'delegationId']) if (input[field] !== undefined && input[field] !== '') readExpenseId(input[field]);
      if (!['assembly_decision', 'delegated_authority'].includes(input.approvalAuthority)) fail();
      text(input.approvalReference, 1000); if (input.confirmDecision !== true) fail();
      // Re-approval of an existing preapproval consumes its stored amount.
      if (input.amount !== undefined) amount(input.amount);
      if (input.allocationId != null && input.allocationId !== '') readExpenseId(input.allocationId);
    }
    if (action === 'amend') {
      readExpenseId(input.categoryId); amount(input.amount); text(input.description, 4000); text(input.note, 4000);
      if (['fundId', 'title', 'kind', 'source'].some(key => Object.hasOwn(input, key))) fail();
    }
    if (action === 'submit_claim') amount(input.amount);
    if (action === 'mark_paid' || ['declare_return', 'record_return'].includes(action)) {
      date(input.paidAt); text(input.paymentReference, 160);
      if (action !== 'mark_paid' && !/^\d{4}-\d{2}-\d{2}T/.test(input.paidAt)) fail();
    }
    if (input.confirmDecision !== undefined && typeof input.confirmDecision !== 'boolean') fail();
    if (['submit_settlement', 'approve_settlement', 'declare_return', 'record_return', 'complete_advance'].includes(action)) {if (input.confirmDecision !== true) fail(); text(input.note, 2000);}
    if (action === 'request_settlement_information') text(input.note, 2000);
    if (action === 'submit_settlement') amount(input.amount, true);
    if (['declare_return', 'record_return'].includes(action)) amount(input.amount);
  }
  return input;
}
export const readExpenseRecoveryIdentifier = value => {
  if (typeof value !== 'string' || !/^expense-recovery_[a-f\d]{24}_[a-f\d]{24}_[a-zA-Z0-9_-]{8,48}$/i.test(value)) fail();
  return value;
};
export function readExpenseUpload(row, file) {
  if (!record(row)) fail(); readExpenseId(row.id); revision(row.revision);
  if (!file || !['image/png', 'image/jpeg', 'application/pdf'].includes(file.type)) fail();
  if (file.size !== undefined && (!Number.isSafeInteger(file.size) || file.size <= 0 || file.size > 5 * 1024 * 1024)) fail();
  const blob = typeof file.arrayBuffer === 'function';
  if (!blob && (!record(file) || typeof file.uri !== 'string' || !file.uri || typeof file.name !== 'string' || !file.name)) fail();
  return file;
}
